import { API_BASE_URL } from "../../config";
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import useFetchUserSeller from "../Utils/useFetchUserSeller";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

const AlertSystem = () => {
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [debugResponse, setDebugResponse] = useState(null);
    const [showDebug, setShowDebug] = useState(false);
    const [selectedSellerId, setSelectedSellerId] = useState("");
    const [selectedAlertCode, setSelectedAlertCode] = useState("");
    const [currentTimeDisplay, setCurrentTimeDisplay] = useState("00:00:00 น.");
    const [deliveryStats, setDeliveryStats] = useState(null);

    const handleLogout = useCallback(() => {
        localStorage.removeItem("user");
        navigate("/login");
    }, [navigate]);

    const userIds = useFetchUserSeller(handleLogout);

    const API_BASE = `${API_BASE_URL}/alert/api/alerts`;
    const STATS_API = `${API_BASE_URL}/seller/api/v1/delivery-stats`;

    useEffect(() => {
        const updateClock = () => {
            const now = new Date();
            const timeString = now.toLocaleTimeString('th-TH', { 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit' 
            });
            setCurrentTimeDisplay(timeString + " น.");
        };
        const timer = setInterval(updateClock, 1000);
        updateClock();
        return () => clearInterval(timer);
    }, []);

    const parseAdditionalData = (dataStr) => {
        if (!dataStr) return {};
        const result = {};
        // Example: "sellerId=U-6BE1D905;customerId=REC-68861C6A;expected=04:42:41;now=05:17:34.788591841;diff=34"
        const pairs = dataStr.split(';');
        pairs.forEach(pair => {
            const [key, value] = pair.split('=');
            if (key) result[key.trim()] = value ? value.trim() : null;
        });
        return result;
    };

    const fetchDeliveryStats = async () => {
        console.log("[DEBUG_LOG] fetchDeliveryStats called, selectedSellerId:", selectedSellerId);
        
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const token = user?.token;
            
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : ''
            };

            // Only add X-USER-UID if a specific seller is selected
            if (selectedSellerId) {
                headers['X-USER-UID'] = selectedSellerId;
                console.log("[DEBUG_LOG] Fetching stats for specific seller:", selectedSellerId);
            } else {
                console.log("[DEBUG_LOG] Fetching overall stats (no seller selected)");
            }

            const resp = await fetch(STATS_API, {
                method: 'GET',
                headers: headers
            });

            console.log("[DEBUG_LOG] API Response status:", resp.status);

            if (resp.ok) {
                const data = await resp.json();
                console.log("[DEBUG_LOG] Delivery Stats received:", data);
                setDeliveryStats(data);
            } else {
                if (resp.status === 401 || resp.status === 403) {
                    handleLogout();
                    return;
                }
                console.error(`Failed to fetch delivery stats: ${resp.status}`);
                // If 404 or other error when no seller, maybe API requires sellerId. 
                // We keep the old stats or set to null.
                if (!selectedSellerId) setDeliveryStats(null);
            }
        } catch (err) {
            console.error(`Error fetching delivery stats: ${err.message}`);
        }
    };

    const fetchAlerts = async () => {
        setLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const token = user?.token;

            const resp = await fetch(API_BASE, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            });

            if (resp.ok) {
                const data = await resp.json();
                setDebugResponse(data);
                
                // Enhance alerts with parsed additionalData
                const enhancedData = data.map(alert => ({
                    ...alert,
                    parsedData: parseAdditionalData(alert.additionalData)
                }));

                // Sort by timeCreated descending (new to old)
                const sortedData = enhancedData.sort((a, b) => {
                    if (!a.timeCreated || !b.timeCreated) return 0;
                    const dateA = new Date(a.timeCreated.replace(' ', 'T'));
                    const dateB = new Date(b.timeCreated.replace(' ', 'T'));
                    return dateB - dateA;
                });
                setAlerts(sortedData);
                setError(null);
            } else {
                if (resp.status === 401 || resp.status === 403) {
                    handleLogout();
                    return;
                }
                setError(`Failed to fetch alerts: ${resp.status}`);
            }
        } catch (err) {
            setError(`Error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const createTestAlert = async () => {
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const token = user?.token;

            const testData = {
                alertType: "SYS",
                severity: "HIGH",
                alertCode: "TEST-ALERT",
                message: "This is a test alert generated at " + new Date().toLocaleString(),
                additionalData: JSON.stringify({ test: true, timestamp: Date.now() })
            };

            const resp = await fetch(API_BASE, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                },
                body: JSON.stringify(testData)
            });

            if (resp.ok) {
                fetchAlerts();
            } else {
                alert(`Failed to create test alert: ${resp.status}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const closeAlert = async (id) => {
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const token = user?.token;

            const resp = await fetch(`${API_BASE}/${id}/close`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? `Bearer ${token}` : ''
                }
            });

            if (resp.ok) {
                fetchAlerts();
            } else {
                alert(`Failed to close alert: ${resp.status}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const closeAllOpenAlerts = async () => {
        const alertsToClose = selectedSellerId 
            ? alerts.filter(alert => alert.status === 'OPEN' && alert.parsedData?.sellerId === selectedSellerId)
            : alerts.filter(alert => alert.status === 'OPEN');

        if (alertsToClose.length === 0) {
            alert("ไม่มีการแจ้งเตือนที่เปิดอยู่");
            return;
        }

        const confirmMessage = selectedSellerId 
            ? `คุณต้องการปิดการแจ้งเตือนทั้งหมด (${alertsToClose.length} รายการ) ของสายส่งนี้ใช่หรือไม่?`
            : `คุณต้องการปิดการแจ้งเตือนที่เปิดอยู่ทั้งหมด (${alertsToClose.length} รายการ) ใช่หรือไม่?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const token = user?.token;

            const closePromises = alertsToClose.map(alert => 
                fetch(`${API_BASE}/${alert.id}/close`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token ? `Bearer ${token}` : ''
                    }
                })
            );

            await Promise.all(closePromises);
            fetchAlerts();
            alert(`ปิดการแจ้งเตือนทั้งหมดของสายส่งแล้ว`);
        } catch (err) {
            alert(`Error closing alerts: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlerts();
        fetchDeliveryStats();
        const interval = setInterval(() => {
            fetchAlerts();
            fetchDeliveryStats();
        }, 10000); // refresh every 10s
        return () => clearInterval(interval);
    }, [selectedSellerId]);

    const filteredAlerts = useMemo(() => {
        let result = alerts;
        if (selectedSellerId) {
            result = result.filter(alert => alert.parsedData?.sellerId === selectedSellerId);
        }
        if (selectedAlertCode) {
            result = result.filter(alert => alert.alertCode === selectedAlertCode);
        }
        return result;
    }, [alerts, selectedSellerId, selectedAlertCode]);

    const stats = useMemo(() => {
        if (deliveryStats) {
            return {
                onTimeToday: deliveryStats.deliveredCount || 0,
                notDelivered: deliveryStats.notDeliveredCount || 0,
                inProgress: deliveryStats.undergoingCount || 0,
                totalStores: deliveryStats.totalStores || 0,
                skipDelivered: deliveryStats.skipDeliveredCount || 0,
                deliveryLate: deliveryStats.deliveryLateCount || 0
            };
        }
        
        // If no deliveryStats and not Show All, return zeros or placeholder?
        // If we want real data, we should prefer 0 over demo numbers when API is active
        const onTimeToday = 0;
        const notDelivered = alerts.filter(a => a.status === 'OPEN' && a.parsedData?.diff > 0).length;
        const inProgress = 0;
        const skipDelivered = 0;
        const deliveryLate = 0;
        return { onTimeToday, notDelivered, inProgress, skipDelivered, deliveryLate };
    }, [alerts, deliveryStats]);

    const sellerOptions = useMemo(() => {
        // Get unique sellerIds from alerts
        const uniqueSellerIds = [...new Set(alerts.map(a => a.parsedData?.sellerId).filter(Boolean))];
        
        // Map to options with names from userIds if available
        const options = uniqueSellerIds.map(id => {
            const user = userIds.find(u => u.uid === id);
            return {
                value: id,
                label: user ? user.nameuser : id,
                username: user ? user.username : ""
            };
        });

        return [
            { value: "", label: "Show All", username: "" },
            ...options
        ];
    }, [alerts, userIds]);

    return (
        <div className="p-2 sm:p-4 md:p-8 bg-slate-50 min-h-screen">
            {/* Header Section */}
            <div className="max-w-full lg:max-w-7xl mx-auto mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-6">
                <div className="text-center md:text-left">
                    <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-800">ระบบติดตามและแจ้งเตือนการส่งน้ำแข็ง</h1>
                    <p className="text-slate-500 font-medium text-xs md:text-sm">โรงน้ำแข็งโตเกียว</p>
                </div>
                
                <div className="flex items-center justify-between md:justify-end gap-3 md:gap-4 bg-white p-3 md:p-4 rounded-2xl shadow-sm border border-slate-200">
                    <div className="text-right flex-1 md:flex-none">
                        <p className="text-[9px] md:text-[10px] text-slate-400 uppercase font-black tracking-widest">เวลาปัจจุบัน</p>
                        <p id="current-time" className="text-lg md:text-xl font-bold text-blue-600 leading-none">{currentTimeDisplay}</p>
                    </div>
                    <div className="h-8 md:h-10 w-[2px] bg-slate-100"></div>
                    <div className="flex flex-col items-end flex-1 md:flex-none">
                        <span className="text-[9px] md:text-[10px] text-red-400 font-black tracking-widest uppercase text-right">ไม่ส่ง</span>
                        <div className="text-red-600 font-bold flex items-center gap-1 md:gap-2 text-sm md:text-base">
                            <span className="relative flex h-2 w-2 md:h-3 md:w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 md:h-3 md:w-3 bg-red-500"></span>
                            </span>
                            {stats.notDelivered} ร้าน
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Statistics */}
            <div className="max-w-full lg:max-w-7xl mx-auto mb-6 md:mb-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 lg:gap-6">
                <div className="bg-white p-3 md:p-6 rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-center sm:items-start lg:items-center gap-2 md:gap-5 transition-all hover:border-green-300 shadow-sm">
                    <div className="bg-green-100 h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl flex items-center justify-center text-green-600 shadow-inner shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-7 md:w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div className="text-center sm:text-left">
                        <p className="text-[10px] md:text-sm text-slate-400 font-bold leading-tight">ส่งตรงเวลา</p>
                        <p className="text-lg md:text-2xl font-black text-slate-800">{stats.onTimeToday} <span className="text-[8px] md:text-base font-bold text-slate-400">{stats.totalStores ? ` / ${stats.totalStores}` : ""}</span></p>
                    </div>
                </div>

                <div className="bg-white p-3 md:p-6 rounded-2xl border border-amber-400 flex flex-col sm:flex-row items-center sm:items-start lg:items-center gap-2 md:gap-5 transition-all shadow-sm bg-amber-50/20">
                    <div className="bg-amber-100 h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl flex items-center justify-center text-amber-600 shadow-inner shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-7 md:w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div className="text-center sm:text-left">
                        <p className="text-[10px] md:text-sm text-amber-600 font-bold leading-tight">ส่งช้า</p>
                        <p className="text-lg md:text-2xl font-black text-slate-800">{stats.deliveryLate} <span className="text-[8px] md:text-base font-bold text-slate-400">ร้าน</span></p>
                    </div>
                </div>

                <div className="bg-white p-3 md:p-6 rounded-2xl border border-red-200 flex flex-col sm:flex-row items-center sm:items-start lg:items-center gap-2 md:gap-5 transition-all hover:border-red-300 shadow-sm bg-red-50/10">
                    <div className="bg-red-100 h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl flex items-center justify-center text-red-600 shadow-inner shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-7 md:w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div className="text-center sm:text-left">
                        <p className="text-[10px] md:text-sm text-red-500 font-bold leading-tight">ไม่ส่ง</p>
                        <p className="text-lg md:text-2xl font-black text-slate-800">{stats.notDelivered} <span className="text-[8px] md:text-base font-bold text-slate-400">ร้าน</span></p>
                    </div>
                </div>

                <div className="bg-white p-3 md:p-6 rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-center sm:items-start lg:items-center gap-2 md:gap-5 transition-all hover:border-blue-300 shadow-sm">
                    <div className="bg-blue-100 h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl flex items-center justify-center text-blue-600 shadow-inner shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-7 md:w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                        </svg>
                    </div>
                    <div className="text-center sm:text-left">
                        <p className="text-[10px] md:text-sm text-slate-400 font-bold leading-tight">อยู่ระหว่างส่ง</p>
                        <p className="text-lg md:text-2xl font-black text-slate-800">{stats.inProgress} <span className="text-[8px] md:text-base font-bold text-slate-400">ร้าน</span></p>
                    </div>
                </div>

                <div className="bg-white p-3 md:p-6 rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-center sm:items-start lg:items-center gap-2 md:gap-5 transition-all hover:border-purple-300 shadow-sm">
                    <div className="bg-purple-100 h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl flex items-center justify-center text-purple-600 shadow-inner shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-7 md:w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                    </div>
                    <div className="text-center sm:text-left">
                        <p className="text-[10px] md:text-sm text-purple-600 font-bold leading-tight">ข้ามร้าน</p>
                        <p className="text-lg md:text-2xl font-black text-slate-800">{stats.skipDelivered} <span className="text-[8px] md:text-base font-bold text-slate-400">ร้าน</span></p>
                    </div>
                </div>
            </div>

            {/* Debug & Test Buttons (Floating or smaller) */}
            <div className="max-w-full lg:max-w-7xl mx-auto mb-4 flex gap-2">
                <button 
                    className="text-[9px] md:text-[10px] bg-slate-200 px-2 md:px-3 py-1 rounded-md text-slate-600 hover:bg-slate-300 transition-colors"
                    onClick={() => setShowDebug(!showDebug)}
                >
                    {showDebug ? "Hide Debug" : "Show Debug"}
                </button>
                <button 
                    className="text-[9px] md:text-[10px] bg-blue-100 px-2 md:px-3 py-1 rounded-md text-blue-600 hover:bg-blue-200 transition-colors"
                    onClick={createTestAlert}
                >
                    Test Alert
                </button>
            </div>

            {/* Filters Menu Section */}
            <div className="max-w-full lg:max-w-7xl mx-auto mb-6 md:mb-8 bg-white p-3 md:p-4 rounded-2xl border border-slate-200 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-slate-500 ml-1">สายส่งน้ำแข็ง</label>
                        <Select
                            options={sellerOptions}
                            value={sellerOptions.find(opt => opt.value === selectedSellerId)}
                            onChange={(opt) => setSelectedSellerId(opt?.value || "")}
                            isClearable
                            placeholder="ทั้งหมดทุกสาย"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: '0.75rem',
                                    padding: '0px',
                                    minHeight: '40px',
                                    backgroundColor: '#f8fafc',
                                    borderColor: '#e2e8f0',
                                    fontSize: '14px'
                                }),
                            }}
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-slate-500 ml-1">ประเภทการแจ้งเตือน</label>
                        <select 
                            className="bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-xl focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 font-medium cursor-pointer transition-all hover:bg-slate-100 h-[40px]"
                            value={selectedAlertCode}
                            onChange={(e) => setSelectedAlertCode(e.target.value)}
                        >
                            <option value="">ทุกประเภท</option>
                            <optgroup label="1. การกำหนดเวลาและตารางเวลา">
                                <option value="DELIVERY_OFF_SCHEDULE">⚠️ ส่งไม่ตรงเวลา (ตามประวัติ)</option>
                                <option value="DELIVERY_LATE_NOT_DELIVERED">⏳ ส่งช้า (ยังไม่สำเร็จ)</option>
                            </optgroup>
                            <optgroup label="2. การเยี่ยมชมลูกค้าและข้ามร้าน">
                                <option value="SKIP_Customer">🛑 ข้ามร้านลูกค้า</option>
                                <option value="Customer_Not_delivery">❌ ไม่ได้ส่งลูกค้า (จบกะแล้ว)</option>
                            </optgroup>
                            <optgroup label="3. การจัดการกะและรายงาน">
                                <option value="REPORT_NOT_DELIVERED">📊 ยังไม่ส่งรายงานการขาย</option>
                                <option value="SHIFT_ALREADY_SUBMITTED">📝 อัปโหลดซ้ำ (ส่งรายงานแล้ว)</option>
                                <option value="SHIFT_ALREADY_VERIFIED">🔒 อัปโหลดซ้ำ (ตรวจสอบแล้ว)</option>
                            </optgroup>
                        </select>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-slate-500 ml-1">ข้อมูลประกอบ</label>
                        <div className="flex items-center gap-2">
                            <div className="text-[10px] text-slate-400 p-2.5 border border-slate-100 rounded-xl bg-slate-50 flex-1 leading-none flex items-center h-[40px]">
                                พบ {filteredAlerts.length} รายการ
                            </div>
                            {alerts.some(a => a.status === 'OPEN' && (!selectedSellerId || a.parsedData?.sellerId === selectedSellerId)) && (
                                <button 
                                    onClick={closeAllOpenAlerts}
                                    className="text-[10px] bg-red-100 text-red-600 hover:bg-red-200 px-3 py-2 rounded-xl font-bold transition-all border border-red-200 h-[40px]"
                                >
                                    {selectedSellerId ? "ปิดทั้งหมดของสายนี้" : "ปิดทั้งหมด"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showDebug && debugResponse && (
                <div className="max-w-full lg:max-w-7xl mx-auto mb-6 bg-slate-900 text-green-400 p-4 rounded-xl overflow-auto max-h-60 text-[10px] md:text-xs font-mono">
                    <h3>Raw API Response:</h3>
                    <pre>{JSON.stringify(debugResponse, null, 2)}</pre>
                </div>
            )}

            {loading && alerts.length === 0 && (
                <div className="flex justify-center p-12">
                    <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-blue-600"></div>
                </div>
            )}
            
            {error && (
                <div className="max-w-full lg:max-w-7xl mx-auto mb-6 bg-red-50 text-red-600 p-4 rounded-xl border border-red-200 text-sm">
                    {error}
                </div>
            )}

            {/* Dashboard Content */}
            <div className="max-w-full lg:max-w-[95%] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                {filteredAlerts.map(alert => {
                    const isCritical = alert.status === 'OPEN';
                    const diff = alert.parsedData?.diff || 0;
                    const expected = alert.parsedData?.expected || "";
                    const actual = alert.parsedData?.actual || alert.parsedData?.now || "";
                    const seller = userIds.find(u => u.uid === alert.parsedData?.sellerId);
                    const sellerName = seller ? seller.nameuser : alert.parsedData?.sellerId || "ไม่ระบุสายส่ง";

                    // Define colors based on severity
                    let bgColor = "bg-red-500";
                    let textColor = "text-red-600";
                    let borderColor = "border-red-500";
                    let shadowColor = "shadow-red-200";
                    let subBgColor = "bg-red-50";
                    let subBorderColor = "border-red-100";
                    let subTextColor = "text-red-700";
                    let labelText = "ช้าไปแล้ว";

                    if (alert.severity === 'LOW') {
                        bgColor = "bg-blue-500";
                        textColor = "text-blue-600";
                        borderColor = "border-blue-500";
                        shadowColor = "shadow-blue-200";
                        subBgColor = "bg-blue-50";
                        subBorderColor = "border-blue-100";
                        subTextColor = "text-blue-700";
                    } else if (alert.severity === 'MEDIUM') {
                        bgColor = "bg-amber-500";
                        textColor = "text-amber-600";
                        borderColor = "border-amber-500";
                        shadowColor = "shadow-amber-200";
                        subBgColor = "bg-amber-50";
                        subBorderColor = "border-amber-100";
                        subTextColor = "text-amber-700";
                    }

                    if (diff < 0) {
                        labelText = "เร็วไป";
                    } else if (diff === 0 && alert.alertType === 'SYS') {
                        labelText = "พบปัญหา";
                    }

                    if (isCritical) {
                        return (
                            <div key={alert.id} className={`bg-white rounded-2xl md:rounded-[2rem] overflow-hidden border-2 ${borderColor} shadow-lg relative transition-transform hover:scale-[1.01]`}>
                                <div className={`${bgColor} text-white px-3 md:px-5 py-1.5 md:py-2 flex justify-between items-center`}>
                                    <div className="flex items-center gap-1.5 md:gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 md:h-4 md:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <span className="text-[10px] md:text-xs font-bold tracking-wider">
                                            {alert.alertCode === 'SKIP_Customer' ? 'ข้ามการส่งสินค้า' :
                                             alert.alertCode === 'Customer_Not_delivery' ? 'ไม่ได้ส่งลูกค้า' :
                                             alert.alertType === 'DELIVERY' ? (diff < 0 ? 'ส่งก่อนเวลา' : 'ส่งล่าช้า') : 
                                             alert.alertCode === 'SHIFT_END_EARLY' ? 'สิ้นสุดกะก่อนเวลา' :
                                             alert.alertCode === 'DELIVERY_TIMING' ? 'แจ้งเตือนเวลาส่ง' :
                                             alert.alertCode}
                                        </span>
                                    </div>
                                    <span className="bg-white text-slate-800 px-1.5 md:px-2 py-0.5 rounded-full text-[8px] md:text-[9px] font-black uppercase shrink-0">{alert.severity} Alert</span>
                                </div>

                                <div className="p-3 md:p-5">
                                    <div className="flex justify-between items-start mb-3 md:mb-4 gap-2">
                                        <div className="flex-1 min-w-0">
                                            <h2 className={`${textColor} text-xs md:text-sm font-black mb-1 uppercase tracking-wide truncate`}>สายส่ง: {sellerName}</h2>
                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                                                <p className="text-[9px] text-slate-400 font-mono">ID: {alert.id}</p>
                                                <p className="text-[9px] text-slate-400 font-bold border-l border-slate-200 pl-2">วันที่: {alert.timeCreated.split(' ')[0]}</p>
                                            </div>
                                            <p className="text-sm md:text-base font-extrabold text-slate-800 leading-tight break-words">
                                                {alert.message}
                                            </p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            {alert.alertCode !== 'SKIP_Customer' && alert.alertCode !== 'Customer_Not_delivery' && (
                                                <div className={`${textColor}`}>
                                                    <p className="text-[7px] md:text-[9px] font-black uppercase tracking-tighter">{labelText}</p>
                                                    <p className="text-xl md:text-3xl font-black leading-none">{Math.abs(diff)}</p>
                                                    <p className="text-[9px] md:text-xs font-bold">นาที</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {alert.alertCode !== 'SKIP_Customer' && alert.alertCode !== 'Customer_Not_delivery' && (
                                        <div className="grid grid-cols-2 gap-2 md:gap-3 mb-3 md:mb-5">
                                            <div className="bg-slate-50 p-2 md:p-3 rounded-xl md:rounded-2xl border border-slate-100 text-center">
                                                <p className="text-[7px] md:text-[9px] text-slate-400 font-bold mb-1 uppercase">เวลาที่ควรส่ง</p>
                                                <p className="text-xs md:text-base font-black text-slate-700">{expected ? expected.substring(0,5) + " น." : ""}</p>
                                            </div>
                                            <div className={`${subBgColor} p-2 md:p-3 rounded-xl md:rounded-2xl border ${subBorderColor} text-center`}>
                                                <p className={`text-[7px] md:text-[9px] ${textColor} font-bold mb-1 uppercase`}>{actual ? "เวลาที่ส่งจริง" : "เวลาที่ตรวจพบ"}</p>
                                                <p className={`text-xs md:text-base font-black ${subTextColor}`}>{actual ? actual.substring(0,5) : alert.timeCreated.split(' ')[1].substring(0,5)} น.</p>
                                            </div>
                                        </div>
                                    )}

                                    {alert.alertCode === 'SKIP_Customer' && (
                                        <div className="bg-amber-50 p-3 rounded-xl border border-amber-100 mb-4">
                                            <div className="flex items-center gap-2 mb-1">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <p className="text-xs font-bold text-amber-700 uppercase">ลำดับร้านที่ข้าม: {alert.parsedData?.orderNumber || "ไม่ระบุ"}</p>
                                            </div>
                                            <p className="text-[10px] text-amber-600 leading-tight">พบการข้ามร้านที่ต้องส่งตามตารางเวลาปัจจุบัน</p>
                                        </div>
                                    )}

                                    <div className="flex gap-2 md:gap-2.5">
                                        <button 
                                            className={`flex-1 ${bgColor} hover:opacity-90 text-white text-xs md:text-sm font-bold py-2 md:py-3 rounded-lg md:rounded-xl shadow-md ${shadowColor} transition-all active:scale-[0.98]`}
                                            onClick={() => closeAlert(alert.id)}
                                        >
                                            รับทราบ / ปิด
                                        </button>
                                        <button className="bg-slate-100 hover:bg-slate-200 text-slate-500 px-3 md:px-4 rounded-lg md:rounded-xl transition-all shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    } else {
                        // History / Resolved / Already Delivered
                        return (
                            <div key={alert.id} className="bg-white rounded-2xl md:rounded-[2rem] overflow-hidden border border-slate-200 shadow-md transition-transform hover:scale-[1.01]">
                                <div className="bg-amber-500 text-white px-3 md:px-5 py-1.5 md:py-2 flex justify-between items-center">
                                    <div className="flex items-center gap-1.5 md:gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 md:h-4 md:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="text-[10px] md:text-xs font-bold tracking-wider">ส่งแล้ว {diff > 0 ? '(ล่าช้า)' : ''}</span>
                                    </div>
                                    <span className="bg-white/20 px-1.5 md:px-2 py-0.5 rounded-full text-[8px] md:text-[9px] font-bold shrink-0">บันทึกประวัติ</span>
                                </div>

                                <div className="p-3 md:p-5">
                                    <div className="flex justify-between items-start mb-3 md:mb-4 gap-2">
                                        <div className="flex-1 min-w-0">
                                            <h2 className="text-slate-400 text-xs md:text-sm font-black mb-1 uppercase tracking-wide truncate">สายส่ง: {sellerName}</h2>
                                            <p className="text-[9px] text-slate-400 font-bold mb-1">วันที่: {alert.timeCreated.split(' ')[0]}</p>
                                            <p className="text-sm md:text-base font-extrabold text-slate-600 leading-tight break-words">{alert.message}</p>
                                        </div>
                                        {diff > 0 && (
                                            <div className="text-right shrink-0">
                                                <div className="text-amber-500">
                                                    <p className="text-[7px] md:text-[9px] font-black uppercase tracking-tighter">ส่งช้าไป</p>
                                                    <p className="text-xl md:text-3xl font-black leading-none">{diff}</p>
                                                    <p className="text-[9px] md:text-xs font-bold">นาที</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 md:gap-3 mb-3 md:mb-5">
                                        <div className="bg-slate-50 p-2 md:p-3 rounded-xl md:rounded-2xl border border-slate-100 text-center">
                                            <p className="text-[7px] md:text-[9px] text-slate-400 font-bold mb-1 uppercase">เวลาที่ควรส่ง</p>
                                            <p className="text-xs md:text-base font-black text-slate-500 italic">{expected ? expected.substring(0,5) + " น." : ""}</p>
                                        </div>
                                        <div className="bg-amber-50 p-2 md:p-3 rounded-xl md:rounded-2xl border border-amber-100 text-center">
                                            <p className="text-[7px] md:text-[9px] text-amber-500 font-bold mb-1 uppercase">{actual ? "ส่งจริงเมื่อ" : "บันทึกเมื่อ"}</p>
                                            <p className="text-xs md:text-base font-black text-amber-700">{actual ? actual.substring(0,5) : alert.timeCreated.split(' ')[1].substring(0,5)} น.</p>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                                        <div className="flex-1 border-2 border-slate-100 bg-slate-50/50 p-2 md:p-3 rounded-lg md:rounded-xl text-[8px] md:text-[10px] text-slate-400 break-all">
                                            ID: {alert.id} <br/>
                                            Resolved: {alert.timeResolved || 'N/A'}
                                        </div>
                                        <button 
                                            className="bg-slate-800 hover:bg-black text-white px-4 md:px-6 py-1.5 md:py-2 rounded-lg md:rounded-xl text-xs md:text-sm font-bold transition-all shrink-0"
                                            onClick={() => closeAlert(alert.id)}
                                            disabled={alert.status !== 'OPEN'}
                                        >
                                            {alert.status === 'OPEN' ? 'ปิด' : 'ปิดแล้ว'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                })}
            </div>

            {filteredAlerts.length === 0 && !loading && (
                <div className="text-center py-20">
                    <div className="bg-slate-100 inline-flex p-6 rounded-full mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 00-2 2H6a2 2 0 00-2-2v-5m16 0h-2.586a1 1 0 01-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 016.586 13H4" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">ไม่พบรายการแจ้งเตือน</h3>
                    <p className="text-slate-500">ลองเปลี่ยนตัวกรองหรือตรวจสอบในภายหลัง</p>
                </div>
            )}
        </div>
    );
};

export default AlertSystem;
